/**
 * Profile Manager
 * Handles profile screen UI, email management, and account deletion entry point.
 * Extracted from auth-manager.js setupAuthHandlers().
 */

import { regenerateRecoveryPhrase } from "../ui/auth/forms.js";
import { t, formatDate } from "../../../shared/i18n/index.js";
import { escapeHtml } from "../../../engine/core/validation.js";

let callbacks = null;

export function init(cbs) {
  callbacks = cbs;
}

/**
 * Setup profile-related DOM event handlers.
 */
export function setupProfileHandlers() {
  const invitations = document.getElementById("profile-email-invitations");
  invitations?.addEventListener("change", async () => {
    const message = document.getElementById("profile-invitations-message");
    const wanted = invitations.checked;
    invitations.disabled = true;
    try {
      const saved = await callbacks.AuthHandlers.api("/api/account/preferences", "PUT", { email_invitations: wanted });
      const user = callbacks.AuthHandlers.getCurrentUser();
      if (user) user.email_invitations = saved.email_invitations;
      invitations.checked = saved.email_invitations;
      if (message) {
        message.textContent = t("profile.invitations.saved");
        message.style.color = "#22c55e";
        message.classList.remove("hidden");
      }
    } catch (err) {
      console.error("[Profile] Could not save invitation emails:", err);
      invitations.checked = !wanted;
      if (message) {
        message.textContent = t("profile.invitations.failed");
        message.style.color = "#ef4444";
        message.classList.remove("hidden");
      }
    } finally {
      invitations.disabled = false;
    }
  });

  // Profile navigation
  const profileBackBtn = document.getElementById("profile-back-btn");
  if (profileBackBtn) {
    profileBackBtn.addEventListener("click", () => {
      callbacks.showScreen("main-app");
    });
  }

  // Profile screen Escape key handler
  const profileScreen = document.getElementById("profile-screen");
  if (profileScreen) {
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !profileScreen.classList.contains("hidden")) {
        // Don't close if a modal is open (e.g., delete account modal)
        const deletionModal = document.getElementById("account-deletion-modal");
        if (deletionModal) return;
        callbacks.showScreen("main-app");
      }
    });
  }

  // Delete account button (in profile)
  const deleteAccountBtn = document.getElementById("delete-account-btn");
  if (deleteAccountBtn) {
    deleteAccountBtn.addEventListener("click", () => {
      callbacks.showDeletionModal();
    });
  }

  // Regenerate recovery phrase — restores phrase-recovery for legacy accounts
  // with no stored verifier, or rotates a lost/compromised phrase. Password
  // wrapping is preserved server-side; only the recovery half changes.
  const regenerateRecoveryBtn = document.getElementById("regenerate-recovery-btn");
  if (regenerateRecoveryBtn) {
    regenerateRecoveryBtn.addEventListener("click", async () => {
      const messageEl = document.getElementById("profile-recovery-message");
      if (messageEl) {
        messageEl.classList.add("hidden");
        messageEl.textContent = "";
      }
      try {
        regenerateRecoveryBtn.disabled = true;
        // Opens the password dialog (which explains the change and can be
        // cancelled), then the save-phrase modal as success feedback.
        await regenerateRecoveryPhrase();
      } catch (err) {
        console.error("[Profile] Regenerate recovery phrase failed:", err);
        if (messageEl) {
          messageEl.textContent = t("account.profile.regenerateFailed", { error: err?.message || String(err) });
          messageEl.classList.remove("hidden");
        }
      } finally {
        regenerateRecoveryBtn.disabled = false;
      }
    });
  }

  // Change password. The endpoint has always existed server-side; this button
  // was never wired to it, so the flow was unreachable from the UI.
  const changePasswordBtn = document.getElementById("change-password-btn");
  if (changePasswordBtn) {
    changePasswordBtn.addEventListener("click", () => {
      showChangePasswordModal(callbacks);
    });
  }

  // Sign out on every device (signing out from the header ends this
  // device's session only).
  document.getElementById("sign-out-everywhere-btn")?.addEventListener("click", async () => {
    const { confirm } = await import("../ui/modals.js");
    const confirmed = await confirm({
      title: t("account.logoutAll.title"),
      message: t("account.logoutAll.message"),
      confirmText: t("account.logoutAll.confirm"),
      confirmClass: "danger",
    });
    if (!confirmed) return;
    callbacks.showScreen("main-app");
    await callbacks.AuthHandlers.logout({ everywhere: true, confirmed: true });
  });

  // Encryption identity check / repair (see interfaces/classic/ui/identity-repair.js).
  document.getElementById("check-identity-btn")?.addEventListener("click", async () => {
    const { checkIdentityNow } = await import("../ui/identity-repair.js");
    await checkIdentityNow();
  });

  // Persistent login ("stay signed in on this device").
  const deviceLogin = callbacks.AuthHandlers?.deviceLogin;
  const deviceToggle = document.getElementById("device-login-toggle");
  const forgetDeviceBtn = document.getElementById("forget-device-btn");
  const deviceMessageEl = document.getElementById("profile-device-message");

  const showDeviceMessage = (text) => {
    if (!deviceMessageEl) return;
    deviceMessageEl.textContent = text;
    deviceMessageEl.classList.remove("hidden");
  };

  if (deviceToggle && deviceLogin) {
    // The checkbox ships checked (the feature is on by default), so reflect the
    // stored preference before the user can act on a wrong state.
    deviceToggle.checked = deviceLogin.isEnabled();

    deviceToggle.addEventListener("change", async () => {
      const enabled = deviceToggle.checked;
      try {
        await deviceLogin.setEnabled(enabled);
        if (enabled) {
          // Turning it back on has nothing to store until the next sign-in —
          // the raw key is not held anywhere this code can reach.
          showDeviceMessage(t("account.profile.deviceOn"));
        } else {
          showDeviceMessage(t("account.profile.deviceOff"));
        }
      } catch (err) {
        console.error("[Profile] Could not change device-login setting:", err);
        deviceToggle.checked = !enabled;
        showDeviceMessage(t("account.profile.deviceSettingFailed", { error: err?.message || String(err) }));
      }
    });
  }

  if (forgetDeviceBtn && deviceLogin) {
    forgetDeviceBtn.addEventListener("click", async () => {
      const original = forgetDeviceBtn.textContent;
      try {
        forgetDeviceBtn.disabled = true;
        forgetDeviceBtn.textContent = t("account.profile.forgetting");
        await deviceLogin.forgetDevice();
        showDeviceMessage(t("account.profile.forgotten"));
      } catch (err) {
        console.error("[Profile] Forget device failed:", err);
        showDeviceMessage(t("account.profile.forgetFailed", { error: err?.message || String(err) }));
      } finally {
        forgetDeviceBtn.disabled = false;
        forgetDeviceBtn.textContent = original;
      }
    });
  }

  const usernameDisplay = document.getElementById("username-display");
  if (usernameDisplay) {
    usernameDisplay.addEventListener("click", () => {
      callbacks.showScreen("profile-screen");
      const user = callbacks.AuthHandlers.getCurrentUser();
      if (user) {
        const profileUsername = document.getElementById("profile-username");
        const profileCreated = document.getElementById("profile-created");
        if (profileUsername) profileUsername.textContent = user.username;
        if (profileCreated && user.created_at) {
          profileCreated.textContent = formatDate(new Date(user.created_at));
        }

        // Invitation emails (PUT /api/account/preferences)
        const invitations = document.getElementById("profile-email-invitations");
        if (invitations) invitations.checked = user.email_invitations !== false;
        document.getElementById("profile-invitations-message")?.classList.add("hidden");
        showIgnored();

        // Email section
        const profileEmail = document.getElementById("profile-email");
        const profileEmailStatus = document.getElementById(
          "profile-email-status",
        );
        const profileEmailStatusField = document.getElementById(
          "profile-email-status-field",
        );
        const resendBtn = document.getElementById("resend-verification-btn");
        const changeEmailBtn = document.getElementById("change-email-btn");

        if (profileEmail) {
          profileEmail.textContent = user.email || t("account.profile.emailNotSet");
        }

        if (user.email) {
          // Show email status
          if (profileEmailStatusField)
            profileEmailStatusField.classList.remove("hidden");
          if (profileEmailStatus) {
            if (user.email_verified) {
              profileEmailStatus.textContent = t("account.profile.emailVerified");
              profileEmailStatus.style.color = "#22c55e";
            } else {
              profileEmailStatus.textContent = t("account.profile.emailPending");
              profileEmailStatus.style.color = "#f59e0b";
            }
          }
          // Show resend button only if not verified
          if (resendBtn) {
            resendBtn.classList.toggle("hidden", user.email_verified);
          }
        } else {
          // No email set
          if (profileEmailStatusField)
            profileEmailStatusField.classList.add("hidden");
          if (resendBtn) resendBtn.classList.add("hidden");
        }

        // Change button text based on whether email exists
        if (changeEmailBtn) {
          changeEmailBtn.textContent = user.email
            ? t("account.profile.changeEmail")
            : t("account.profile.addEmail");
        }
      }
    });
  }

  // Resend verification email button
  const resendVerificationBtn = document.getElementById(
    "resend-verification-btn",
  );
  if (resendVerificationBtn) {
    resendVerificationBtn.addEventListener("click", async () => {
      const messageEl = document.getElementById("profile-email-message");
      try {
        resendVerificationBtn.disabled = true;
        resendVerificationBtn.textContent = t("account.profile.sending");

        const response = await fetch("/api/auth/resend-verification", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || t("account.profile.sendFailed"));
        }

        if (messageEl) {
          messageEl.textContent = t("account.profile.verificationSent");
          messageEl.style.color = "#22c55e";
          messageEl.classList.remove("hidden");
        }
      } catch (err) {
        if (messageEl) {
          messageEl.textContent = err.message;
          messageEl.style.color = "#ef4444";
          messageEl.classList.remove("hidden");
        }
      } finally {
        resendVerificationBtn.disabled = false;
        resendVerificationBtn.textContent = t("html.profile.resendVerification");
      }
    });
  }

  // Change email button
  const changeEmailBtn = document.getElementById("change-email-btn");
  if (changeEmailBtn) {
    changeEmailBtn.addEventListener("click", () => {
      const user = callbacks.AuthHandlers.getCurrentUser();
      const currentEmail = user?.email || "";
      const newEmail = prompt(t("account.profile.newEmailPrompt"), currentEmail);

      if (newEmail === null) return; // Cancelled
      if (newEmail === currentEmail) return; // No change

      // Send update request
      (async () => {
        const messageEl = document.getElementById("profile-email-message");
        try {
          changeEmailBtn.disabled = true;
          changeEmailBtn.textContent = t("account.profile.updating");

          const response = await fetch("/api/auth/resend-verification", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
            body: JSON.stringify({ email: newEmail || null }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || t("account.profile.updateEmailFailed"));
          }

          // Update local user state
          if (user) {
            user.email = newEmail || null;
            user.email_verified = false;
          }

          // Update UI
          const profileEmail = document.getElementById("profile-email");
          const profileEmailStatus = document.getElementById(
            "profile-email-status",
          );
          const profileEmailStatusField = document.getElementById(
            "profile-email-status-field",
          );
          const resendBtn = document.getElementById("resend-verification-btn");

          if (profileEmail) profileEmail.textContent = newEmail || t("account.profile.emailNotSet");

          if (newEmail) {
            if (profileEmailStatusField)
              profileEmailStatusField.classList.remove("hidden");
            if (profileEmailStatus) {
              profileEmailStatus.textContent = t("account.profile.emailPending");
              profileEmailStatus.style.color = "#f59e0b";
            }
            if (resendBtn) resendBtn.classList.remove("hidden");
            changeEmailBtn.textContent = t("account.profile.changeEmail");

            if (messageEl) {
              messageEl.textContent = t("account.profile.emailUpdated");
              messageEl.style.color = "#22c55e";
              messageEl.classList.remove("hidden");
            }
          } else {
            if (profileEmailStatusField)
              profileEmailStatusField.classList.add("hidden");
            if (resendBtn) resendBtn.classList.add("hidden");
            changeEmailBtn.textContent = t("account.profile.addEmail");

            if (messageEl) {
              messageEl.textContent = t("account.profile.emailRemoved");
              messageEl.style.color = "#22c55e";
              messageEl.classList.remove("hidden");
            }
          }
        } catch (err) {
          if (messageEl) {
            messageEl.textContent = err.message;
            messageEl.style.color = "#ef4444";
            messageEl.classList.remove("hidden");
          }
        } finally {
          changeEmailBtn.disabled = false;
        }
      })();
    });
  }

  console.log("[ProfileManager] Profile handlers initialized");
}

/**
 * Change-password dialog.
 *
 * Built here rather than in index.html because it only exists while open —
 * a form holding two password fields should not sit in the DOM for the whole
 * session. Reuses `.unlock-modal`, which already scrolls safely on short
 * viewports (see the scroll-safe overlay block in main.css).
 */
/**
 * The people whose invitations this account ignores ("Always ignore" on an
 * invitation; handlers/shares/ignores.rs), each with "Stop ignoring".
 */
async function showIgnored() {
  const list = document.getElementById("profile-ignored-list");
  if (!list) return;
  list.replaceChildren();
  let people = [];
  try {
    people = await callbacks.AuthHandlers.api("/api/invitation-ignores", "GET");
  } catch (err) {
    console.warn("[Profile] Could not load ignored invitations:", err);
    return;
  }
  if (!people.length) {
    const none = document.createElement("span");
    none.className = "profile-ignored-none";
    none.textContent = t("profile.ignored.none");
    list.append(none);
    return;
  }
  for (const person of people) {
    const row = document.createElement("div");
    row.className = "profile-ignored-item";
    const name = document.createElement("span");
    name.textContent = person.username;
    const stop = document.createElement("button");
    stop.type = "button";
    stop.className = "btn-secondary btn-small";
    stop.textContent = t("profile.ignored.stop");
    stop.addEventListener("click", async () => {
      stop.disabled = true;
      try {
        await callbacks.AuthHandlers.api(`/api/invitation-ignores/${encodeURIComponent(person.user_id)}`, "DELETE");
        showIgnored();
      } catch (err) {
        console.error("[Profile] Could not stop ignoring:", err);
        stop.disabled = false;
        alert(t("profile.ignored.failed"));
      }
    });
    row.append(name, stop);
    list.append(row);
  }
}

function showChangePasswordModal(cbs) {
  const existing = document.getElementById("change-password-modal");
  if (existing) existing.remove();

  const modal = document.createElement("div");
  modal.className = "unlock-modal";
  modal.id = "change-password-modal";
  modal.innerHTML = `
    <div class="unlock-modal-content">
      <button class="unlock-close-btn" id="cp-close" title="${escapeHtml(t("common.cancel"))}" aria-label="${escapeHtml(t("common.cancel"))}">&times;</button>
      <h2>${escapeHtml(t("html.profile.changePassword"))}</h2>
      <p>${escapeHtml(t("account.changePassword.intro"))}</p>
      <div class="unlock-error hidden" id="cp-error"></div>
      <form class="unlock-form" id="cp-form">
        <input type="password" id="cp-current" class="auth-input" autocomplete="current-password"
               placeholder="${escapeHtml(t("account.changePassword.current"))}" required />
        <input type="password" id="cp-new" class="auth-input" autocomplete="new-password"
               placeholder="${escapeHtml(t("account.changePassword.new"))}" required minlength="8" />
        <input type="password" id="cp-confirm" class="auth-input" autocomplete="new-password"
               placeholder="${escapeHtml(t("account.changePassword.confirm"))}" required minlength="8" />
        <div class="cp-notice">
          <strong>${escapeHtml(t("account.changePassword.noticeTitle"))}</strong>
          <small>${escapeHtml(t("account.changePassword.noticeText"))}</small>
        </div>
        <div style="display:flex; gap:8px;">
          <button type="button" class="auth-btn" id="cp-cancel" style="background:#6b7280;">${escapeHtml(t("common.cancel"))}</button>
          <button type="submit" class="auth-btn" style="flex:1;">${escapeHtml(t("html.profile.changePassword"))}</button>
        </div>
      </form>
    </div>
  `;
  document.body.appendChild(modal);

  const close = () => modal.remove();
  document.getElementById("cp-close").addEventListener("click", close);
  document.getElementById("cp-cancel").addEventListener("click", close);

  const errorEl = document.getElementById("cp-error");
  const fail = (msg) => {
    errorEl.textContent = msg;
    errorEl.classList.remove("hidden");
  };

  document.getElementById("cp-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    errorEl.classList.add("hidden");

    const current = document.getElementById("cp-current").value;
    const next = document.getElementById("cp-new").value;
    const confirmValue = document.getElementById("cp-confirm").value;

    if (next !== confirmValue) return fail(t("account.changePassword.mismatch"));
    if (next.length < 8) return fail(t("account.changePassword.tooShort"));
    if (next === current) return fail(t("account.changePassword.same"));

    const submitBtn = e.target.querySelector('button[type="submit"]');
    const original = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = t("account.changePassword.changing");

    try {
      await cbs.AuthHandlers.changePassword(current, next);
      close();
      const messageEl = document.getElementById("profile-device-message");
      if (messageEl) {
        messageEl.textContent = t("account.changePassword.done");
        messageEl.classList.remove("hidden");
      }
    } catch (err) {
      console.error("[Profile] Change password failed:", err);
      fail(err?.message || t("account.changePassword.failed"));
      submitBtn.disabled = false;
      submitBtn.textContent = original;
    } finally {
      // Don't leave either password sitting in the DOM.
      const currentEl = document.getElementById("cp-current");
      const newEl = document.getElementById("cp-new");
      const confirmEl = document.getElementById("cp-confirm");
      if (currentEl) currentEl.value = "";
      if (newEl) newEl.value = "";
      if (confirmEl) confirmEl.value = "";
    }
  });

  document.getElementById("cp-current").focus();
}
